import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Settings, Save, CreditCard, ArrowLeft, Loader2, CheckCircle, AlertCircle, Key, Eye, EyeOff, Palette } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { useLot } from '../contexts/LotContext';
import BrandingStep from './BrandingStep';

interface LotSettings {
  id: string;
  lot_id: string;
  hourly_rate: number;
  max_daily_rate: number;
  max_hours: number;
  subscription_amount: number;
  scheduled_subscriptions_enabled: boolean;
  max_monthly_subscriptions: number | null;
  notification_threshold: number;
  notification_enabled: boolean;
  public_site_url: string;
  updated_at: string;
}

interface ConnectAccountStatus {
  charges_enabled: boolean;
  details_submitted: boolean;
  payouts_enabled: boolean;
  requirements: {
    currently_due: string[];
    eventually_due: string[];
    past_due: string[];
  };
}

export default function AdminSettings() {
  const { lotSlug } = useParams<{ lotSlug: string }>();
  const { lot, settings, userRole, isLoading: lotLoading } = useLot();
  const [localSettings, setLocalSettings] = useState<LotSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [connectAccountStatus, setConnectAccountStatus] = useState<ConnectAccountStatus | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form state
  const [hourlyRate, setHourlyRate] = useState(4.5);
  const [maxDailyRate, setMaxDailyRate] = useState(16.5);
  const [maxHours, setMaxHours] = useState(12);
  const [subscriptionAmount, setSubscriptionAmount] = useState(150);
  const [monthlyEnabled, setMonthlyEnabled] = useState(true);
  const [notificationThreshold, setNotificationThreshold] = useState(90);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const [scheduledSubscriptionsEnabled, setScheduledSubscriptionsEnabled] = useState(false);
  const [maxMonthlySubscriptions, setMaxMonthlySubscriptions] = useState<number | string>('');
  const [logoUrl, setLogoUrl] = useState('');
  const [brandColor, setBrandColor] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (settings) {
      setHourlyRate(settings.hourly_rate);
      setMaxDailyRate(settings.max_daily_rate);
      setMaxHours(settings.max_hours);
      setSubscriptionAmount(settings.subscription_amount || 150);
      setMonthlyEnabled(settings.monthly_enabled !== false);
      setNotificationThreshold(settings.notification_threshold || 90);
      setNotificationEnabled(settings.notification_enabled);
      setScheduledSubscriptionsEnabled(settings.scheduled_subscriptions_enabled || false);
      setMaxMonthlySubscriptions(settings.max_monthly_subscriptions ?? '');
      setLogoUrl(settings.logo_url ?? '');
      setBrandColor(settings.brand_color ?? '');
      setLocalSettings({
        ...settings,
        public_site_url: settings.public_site_url || ''
      });
    }
  }, [settings]);

  // Stripe Connect callback handling removed - now redirects to success page

  const resetToDefaults = () => {
    const defaultData = {
      id: localSettings?.id || '',
      lot_id: lot?.id || '',
      hourly_rate: 4.5,
      max_daily_rate: 16.5,
      max_hours: 12,
      subscription_amount: 150,
      scheduled_subscriptions_enabled: false,
      max_monthly_subscriptions: null,
      notification_threshold: 90,
      notification_enabled: true,
      public_site_url: localSettings?.public_site_url || '',
      updated_at: new Date().toISOString(),
    };

    setHourlyRate(defaultData.hourly_rate);
    setMaxDailyRate(defaultData.max_daily_rate);
    setMaxHours(defaultData.max_hours);
    setSubscriptionAmount(defaultData.subscription_amount);
    setNotificationThreshold(defaultData.notification_threshold);
    setNotificationEnabled(defaultData.notification_enabled);
    setScheduledSubscriptionsEnabled(defaultData.scheduled_subscriptions_enabled);
    setMaxMonthlySubscriptions(defaultData.max_monthly_subscriptions ?? '');
    setLocalSettings(defaultData);

    toast.success('Settings reset to defaults');
  };

  const copyFromProduction = async () => {
    try {
      setLoading(true);
      
      // This would typically fetch from production settings
      // For now, we'll set some reasonable defaults
      const prodData = {
        hourly_rate: 4.5,
        max_daily_rate: 16.5,
        max_hours: 12,
        notification_threshold: 90,
        notification_enabled: true,
      };

      setHourlyRate(prodData.hourly_rate);
      setMaxDailyRate(prodData.max_daily_rate);
      setMaxHours(prodData.max_hours);
      setNotificationThreshold(prodData.notification_threshold);
      setNotificationEnabled(prodData.notification_enabled);

      toast.success('Production settings copied successfully');
    } catch (error) {
      console.error('Error copying production settings:', error);
      toast.error('Failed to copy production settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!lot || !localSettings) {
      toast.error('Lot information not available');
      return;
    }

    setLoading(true);

    try {
      const updatedSettings = {
        hourly_rate: hourlyRate,
        max_daily_rate: maxDailyRate,
        max_hours: maxHours,
        subscription_amount: subscriptionAmount,
        monthly_enabled: monthlyEnabled,
        scheduled_subscriptions_enabled: scheduledSubscriptionsEnabled,
        max_monthly_subscriptions: maxMonthlySubscriptions === '' ? null : Number(maxMonthlySubscriptions),
        notification_threshold: notificationThreshold,
        notification_enabled: notificationEnabled,
        logo_url: logoUrl || null,
        brand_color: brandColor || null,
      };

      const { data, error } = await supabase
        .from('lot_settings')
        .update(updatedSettings)
        .eq('lot_id', lot.id)
        .select()
        .single();

      if (error) {
        console.error('Settings update error:', error);
        throw error;
      }

      setLocalSettings(data);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectAccountStatus = async () => {
    if (!lot?.stripe_account_id) return;

    try {
      setConnectLoading(true);
      const apiUrl = window.location.hostname === 'localhost' 
        ? '/api/create-connect-account'
        : '/.netlify/functions/create-connect-account';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'get_account_status',
          lotId: lot.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch account status');
      }

      const data = await response.json();
      setConnectAccountStatus(data.account);
    } catch (error) {
      console.error('Error fetching Connect account status:', error);
      // Don't show error toast for this, it's not critical
    } finally {
      setConnectLoading(false);
    }
  };

  const createOnboardingLink = async () => {
    if (!lot) return;

    try {
      setConnectLoading(true);
      const apiUrl = window.location.hostname === 'localhost' 
        ? '/api/create-connect-account'
        : '/.netlify/functions/create-connect-account';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create_onboarding_link',
          lotId: lot.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create onboarding link');
      }

      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error creating onboarding link:', error);
      toast.error('Failed to create setup link');
    } finally {
      setConnectLoading(false);
    }
  };

  const createDashboardLink = async () => {
    if (!lot) return;

    try {
      setConnectLoading(true);
      const apiUrl = window.location.hostname === 'localhost' 
        ? '/api/create-connect-account'
        : '/.netlify/functions/create-connect-account';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create_login_link',
          lotId: lot.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create dashboard link');
      }

      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error creating dashboard link:', error);
      toast.error('Failed to access payment dashboard');
    } finally {
      setConnectLoading(false);
    }
  };

  // Load Connect account status when component mounts
  useEffect(() => {
    if (lot) {
      fetchConnectAccountStatus();
    }
  }, [lot]);

  const getSetupButtonText = () => {
    if (!lot?.stripe_account_id) {
      return 'Set Up Payments';
    }
    
    if (!connectAccountStatus) {
      return 'Complete Setup';
    }
    
    const isFullySetup = connectAccountStatus.charges_enabled && 
                        connectAccountStatus.payouts_enabled && 
                        connectAccountStatus.details_submitted &&
                        connectAccountStatus.requirements.currently_due.length === 0;
    
    return isFullySetup ? 'Edit Payment Setup' : 'Complete Setup';
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setPasswordLoading(true);

      // First, sign in with current password to verify it
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: (await supabase.auth.getUser()).data.user?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        toast.error('Current password is incorrect');
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        console.error('Password update error:', updateError);
        toast.error('Failed to update password. Please try again.');
      } else {
        toast.success('Password updated successfully! Refreshing session...');
        // Reset form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordForm(false);
        
        // Force a page reload to refresh the session after password change
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Check permissions
  if (userRole !== 'owner' && userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access settings for this lot.
            </p>
            <Link
              to={`/admin/lots/${lotSlug}/dashboard`}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (lotLoading || !lot || !localSettings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <Link
                to={`/admin/lots/${lotSlug}/dashboard`}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
                <p className="text-sm text-gray-600">{lot.display_name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Settings className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">Lot Configuration</h2>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={resetToDefaults}
                  className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1 border border-gray-300 rounded"
                  disabled={loading}
                >
                  Reset to Defaults
                </button>
                <button
                  onClick={copyFromProduction}
                  className="text-sm text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-300 rounded"
                  disabled={loading}
                >
                  Copy from Production
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Password Change Section */}
            <div className="space-y-4 border-b border-gray-200 pb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Key className="h-5 w-5 text-gray-600 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">Account Security</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="text-sm text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-300 rounded"
                >
                  {showPasswordForm ? 'Cancel' : 'Change Password'}
                </button>
              </div>

              {showPasswordForm && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Current Password
                        </label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            required
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter current password"
                            disabled={passwordLoading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            required
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter new password"
                            disabled={passwordLoading}
                            minLength={6}
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            required
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Confirm new password"
                            disabled={passwordLoading}
                            minLength={6}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={passwordLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                      >
                        {passwordLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            Update Password
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* Pricing Settings */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Pricing</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Hourly Rate ($)
                  </label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max Daily Rate ($)
                  </label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={maxDailyRate}
                    onChange={(e) => setMaxDailyRate(parseFloat(e.target.value) || 0)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Max Hours
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={maxHours}
                    onChange={(e) => setMaxHours(parseInt(e.target.value) || 1)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>
              </div>
              
              {/* Monthly Subscription */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Monthly Parking</h3>
                  <button
                    type="button"
                    onClick={() => setMonthlyEnabled(v => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${monthlyEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                    role="switch"
                    aria-checked={monthlyEnabled}
                    disabled={loading}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${monthlyEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  {monthlyEnabled ? 'Customers can purchase monthly subscriptions on your lot page.' : 'Monthly parking is hidden. Customers will only see hourly/daily options.'}
                </p>
                {monthlyEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Monthly Rate ($)
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={subscriptionAmount}
                      onChange={(e) => setSubscriptionAmount(parseFloat(e.target.value) || 150)}
                      className="mt-1 w-full md:w-1/3 rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                    />
                    <p className="mt-1 text-sm text-gray-600">
                      CAD per month, charged automatically via Stripe.
                    </p>
                  </div>
                )}
              </div>

              {/* Scheduled Subscriptions */}
              <div className="space-y-3 border-t border-gray-100 pt-4">
                <h3 className="text-lg font-medium text-gray-900">Scheduled Subscriptions</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="scheduledSubscriptionsEnabled"
                    checked={scheduledSubscriptionsEnabled}
                    onChange={(e) => setScheduledSubscriptionsEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <label htmlFor="scheduledSubscriptionsEnabled" className="text-sm text-gray-700">
                    Allow customers to schedule a future subscription start date
                  </label>
                </div>
                {scheduledSubscriptionsEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Max Monthly Subscriptions
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={maxMonthlySubscriptions}
                      onChange={(e) => setMaxMonthlySubscriptions(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                      className="mt-1 w-full md:w-1/3 rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Unlimited"
                      disabled={loading}
                    />
                    <p className="mt-1 text-sm text-gray-600">
                      Maximum subscriptions allowed per month. Leave empty for unlimited.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Pricing Logic:</strong> Users pay the hourly rate up to the maximum hours. After that, they pay the maximum daily rate.
                  Maximum number of hours a user can park. After 3 hours, the maximum daily rate applies.
                </p>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-green-800 mb-3">
                  <strong>SMS Notifications:</strong> We handle SMS notifications for your customers through our platform service. 
                  Your customers will receive parking reminders and updates automatically.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notification Threshold (minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="180"
                    value={notificationThreshold}
                    onChange={(e) => setNotificationThreshold(parseInt(e.target.value) || 90)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    How many minutes before expiry to send notification
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="notificationEnabled"
                    checked={notificationEnabled}
                    onChange={(e) => setNotificationEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <label htmlFor="notificationEnabled" className="ml-2 block text-sm text-gray-900">
                    Enable notifications
                  </label>
                </div>
              </div>
            </div>

            {(userRole === 'admin' || userRole === 'owner') && (
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="h-5 w-5 text-gray-600" />
                  <h2 className="text-xl font-semibold text-gray-900">Payment Account</h2>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  Set up a Stripe Connect account to receive payments directly to your bank account.
                  <br />
                  <strong>Platform Fee:</strong> ParkConfetti charges a 10% platform fee on all parking sessions.
                </p>

                {connectAccountStatus === null ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-gray-600">Checking account status...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Account Status</h3>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {connectAccountStatus.charges_enabled ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                          )}
                          <span className="text-sm">
                            Charges: {connectAccountStatus.charges_enabled ? 'Enabled' : 'Pending'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {connectAccountStatus.payouts_enabled ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                          )}
                          <span className="text-sm">
                            Payouts: {connectAccountStatus.payouts_enabled ? 'Enabled' : 'Pending'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {connectAccountStatus.details_submitted ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                          )}
                          <span className="text-sm">
                            Details: {connectAccountStatus.details_submitted ? 'Submitted' : 'Incomplete'}
                          </span>
                        </div>
                      </div>

                      {connectAccountStatus.requirements.currently_due.length > 0 && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                          <p className="text-sm font-medium text-yellow-800">Action Required:</p>
                          <p className="text-sm text-yellow-700">
                            Complete your account setup to enable all features.
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={createOnboardingLink}
                        disabled={connectLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {connectLoading ? 'Loading...' : getSetupButtonText()}
                      </button>
                      
                      {connectAccountStatus.charges_enabled && (
                        <button
                          type="button"
                          onClick={createDashboardLink}
                          disabled={connectLoading}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {connectLoading ? 'Loading...' : 'View Payment Dashboard'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Branding Section */}
            <div className="space-y-4 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Palette className="h-5 w-5 text-gray-600" />
                <h2 className="text-xl font-semibold text-gray-900">Branding</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Customize your lot page with your own logo and brand color. If set, a "Powered by ParkConfetti" badge will appear in the footer so customers know the platform.
              </p>
              <BrandingStep
                lotName={lot.display_name}
                logoUrl={logoUrl}
                brandColor={brandColor}
                onLogoChange={setLogoUrl}
                onColorChange={setBrandColor}
                allowUpload={true}
                lotId={lot.id}
              />
            </div>

            {/* Save Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}